import { isUndefined } from 'lodash-es';
import { type KeyboardEvent } from 'react';

import { HistogramSelectionAreaTooltip } from './HistogramSelectionAreaTooltip.js';
import { HistogramSelectionDatapoints } from './HistogramSelectionDatapoints.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { reduceExploreSelect } from '../../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { DynamicSelectionArea } from '../../../../core/components/selection-area/DynamicSelectionArea.js';
import type { RelativeAbsolutePosition } from '../../../../core/types/pointer-events.js';
import { getPercentageFromPixel } from '../../../../core/utils/get-percentage-from-pixel.js';
import { getSelectionPixels } from '../../../../core/utils/get-selection-pixels.js';
import { useSelectState } from '../../../hooks/store/selectors.js';
import { useSetState } from '../../../hooks/store/state-setters.js';
import { useHistogramChartOverlayRef } from '../../../hooks/useHistogramChartOverlayRef.js';
import { useHistogramInteractions } from '../../../hooks/useHistogramInteractions.js';
import { useHistogramSelectionAreaRef } from '../../../hooks/useHistogramSelectionAreaRef.js';
import { useHistogramXFormatter } from '../../../hooks/useHistogramXFormatter.js';
import { useHistogramXScale } from '../../../hooks/useHistogramXScale.js';
import { useXAxisConfig } from '../../../hooks/useXAxisConfig.js';

export const HistogramSelectionArea = () => {
  const bottomFormatter = useHistogramXFormatter();
  const selectionAreaRef = useHistogramSelectionAreaRef();
  const { unit: xUnit } = useXAxisConfig();
  const { xScale } = useHistogramXScale();
  const chartRef = useHistogramChartOverlayRef();
  const setState = useSetState();
  const selectState = useSelectState();
  const { width } = useGridPlotSize();
  /* eslint-disable-next-line @typescript-eslint/no-deprecated */
  const { zoomX } = useHistogramInteractions();

  const hasMagnifiers = !isUndefined(bottomFormatter);

  if (isUndefined(selectState) || !chartRef.current) {
    return null;
  }

  const { start, end, selectionPercentage, finished, accessible, variant } =
    selectState;

  const handleWrapperKeyDown = (event: KeyboardEvent) => {
    const canZoom = zoomX?.enabled;
    if (event.key === 'Enter' && canZoom) {
      chartRef.current?.focus();
    }
  };

  const handleSelectionChange = ({
    selection,
    accessible,
  }: {
    selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
    accessible: boolean;
  }) => {
    setState(
      reduceExploreSelect({
        start: selection[0],
        end: selection[1],
        finished: true,
        accessible,
        variant,
        selectionPercentage: getPercentageFromPixel(selection, width),
      }),
    );
  };

  const selectionPosition: [
    RelativeAbsolutePosition,
    RelativeAbsolutePosition,
  ] = finished
    ? getSelectionPixels(selectionPercentage, width, [start, end])
    : [start, end];

  return (
    <>
      {variant === 'select' ? (
        <>
          <HistogramSelectionDatapoints />
          <HistogramSelectionAreaTooltip />
        </>
      ) : null}
      <DynamicSelectionArea
        chartRef={chartRef}
        selection={selectionPosition}
        hasMagnifiers={hasMagnifiers}
        variant={variant}
        focusable={accessible}
        interactable={finished}
        bottomFormatter={(value) =>
          bottomFormatter(xScale.invert(value), xUnit)
        }
        onKeyDown={handleWrapperKeyDown}
        onSelectionChange={handleSelectionChange}
        selectionAreaRef={selectionAreaRef}
      />
    </>
  );
};
